/**
 * Играет ли запись — до того, как показать ученику плеер и «Отправить» (13.09.2026).
 *
 * 🔴 ЗАЧЕМ. У битого файла декод длины падает, и блок записи говорил «длину
 * проверить не удалось — отправить можно», хотя плеер под ним тут же писал «The
 * recording didn't load». Ученик отправлял, сервер отвечал «файл не получился», и
 * так по кругу. «Сбой замера ответ не отклоняет» (договор `record-integrity.md`
 * §2.2) остаётся правдой — но только для файла, который браузер реально играет.
 *
 * ⚠️ НИКОГДА НЕ БРОСАЕТ И НЕ ВИСНЕТ. Проверка стоит между записью и отправкой:
 * зависание навсегда заперло бы кнопку, исключение отняло бы запись. Тишина
 * дольше `PLAYABLE_TIMEOUT_MS` — «не играет».
 *
 * ⚠️ ТОЛЬКО МЕТАДАННЫЕ. Звук не включается и байты сверх заголовка не качаются:
 * `preload="metadata"`, и элемент в документ не вставляется.
 */

export const PLAYABLE_TIMEOUT_MS = 5000;

export type PlayableDeps = {
  /** Подменяется в проверках; в браузере — новый `<audio>`. */
  makeAudio?: () => HTMLAudioElement;
  timeoutMs?: number;
};

export function probePlayable(url: string, deps: PlayableDeps = {}): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let el: HTMLAudioElement;
    try {
      el = (deps.makeAudio ?? (() => document.createElement('audio')))();
    } catch {
      resolve(false);
      return;
    }

    let done = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (playable: boolean) => {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      el.removeEventListener('loadedmetadata', yes);
      el.removeEventListener('canplay', yes);
      el.removeEventListener('error', no);
      // Ссылку снимаем, чтобы элемент не держал файл в памяти после ответа.
      try {
        el.removeAttribute('src');
        el.load();
      } catch {
        /* снимать нечего */
      }
      resolve(playable);
    };
    const yes = () => finish(true);
    const no = () => finish(false);

    el.addEventListener('loadedmetadata', yes);
    el.addEventListener('canplay', yes);
    el.addEventListener('error', no);
    timer = setTimeout(no, deps.timeoutMs ?? PLAYABLE_TIMEOUT_MS);
    try {
      el.preload = 'metadata';
      el.src = url;
    } catch {
      finish(false);
    }
  });
}
